import { useSearchParams } from "react-router-dom";
import { universeByKey } from "@/data/universes";
import React, { useEffect, useMemo, useState } from "react";
import { feeLabel } from "@/lib/proApi";
import { Link } from "react-router-dom";
import { Search, Lock, ArrowRight, Truck, FileText, PackageCheck } from "lucide-react";
import { proApi, eurNum, imgSrc } from "@/lib/proApi";
import { usePro } from "@/context/ProContext";
import { PendingNotice } from "@/pages/pro/ProLayout";
import { useSeo } from "@/lib/seo";
import { ProQuickReorder } from "@/components/ProQuickReorder";

const Perks = ({ config }) => (
  <div className="grid sm:grid-cols-3 gap-3 mt-8">
    {[
      [PackageCheck, "Quantités au choix", "De 12 à 180 pièces selon le produit"],
      [Truck, "Livraison offerte", `Mondial Relay ${feeLabel(config?.mondial_relay_fee_ht, "offert")} et transporteur ${feeLabel(config?.carrier_fee_ht, "offert")} en métropole dès 300 € HT d'achat (sinon retrait au dépôt de Chatou) — 10 à 15 jours. ${config?.dom_tom_fees || ""}`],
      [FileText, "Factures PDF", "TVA détaillée, téléchargeables à tout moment"],
    ].map(([Icon, t, d]) => (
      <div key={t} className="flex gap-3 rounded-2xl bg-white/5 border border-white/15 p-4">
        <Icon className="w-5 h-5 text-[var(--oz-gold)] flex-shrink-0" />
        <div><p className="font-semibold text-sm">{t}</p><p className="text-xs text-white/65 mt-0.5">{d}</p></div>
      </div>
    ))}
  </div>
);

const ProductCard = ({ p, approved }) => (
  <Link to={`/espace-pro/produit/${p.slug}`} data-testid={`pro-product-card-${p.slug}`}
    className="group rounded-2xl bg-white border border-[var(--oz-border)] overflow-hidden hover:border-[var(--oz-gold)] transition-colors flex flex-col">
    <div className="aspect-square bg-[var(--oz-bg-2)] overflow-hidden">
      <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
    </div>
    <div className="p-4 flex-1 flex flex-col">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--oz-gold)] font-semibold">{p.category}</span>
      <h3 className="mt-1 font-semibold text-[var(--oz-text)] leading-snug">{p.name}</h3>
      <p className="mt-1 text-xs text-[var(--oz-muted)]">{p.pack_sizes.join(" ou ")} pièces</p>
      <div className="mt-auto pt-3 flex items-center justify-between">
        {approved ? (
          <span data-testid={`pro-product-price-${p.slug}`} className="font-semibold text-[var(--oz-text)]">{eurNum(p.unit_price_ht)} <span className="text-xs text-[var(--oz-muted)]">HT / pièce</span></span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--oz-muted)]"><Lock className="w-3.5 h-3.5" /> Prix réservés aux pros</span>
        )}
        <ArrowRight className="w-4 h-4 text-[var(--oz-gold)]" />
      </div>
    </div>
  </Link>
);

export default function ProCatalog() {
  const { user, approved, config } = usePro();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Tous");
  const [params, setParams] = useSearchParams();
  const univ = universeByKey(params.get("univers"));
  useSeo({ title: "Espace Pro Ozentya — commande en gros pour coiffeurs et revendeurs", description: "Boutique professionnelle Ozentya : produits capillaires et barbe en lots, prix HT, livraison Mondial Relay offerte, factures PDF.", path: "/espace-pro" });

  useEffect(() => {
    if (user === null) return;
    proApi.get("/pro/products").then(({ data }) => setProducts(data));
  }, [user]);

  const cats = useMemo(() => ["Tous", ...new Set(products.map((p) => p.category))], [products]);
  const shown = products.filter((p) => (!univ || p.universes?.includes(univ.key)) && (cat === "Tous" || p.category === cat) && p.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div>
      <section className="bg-[var(--oz-dark)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14 md:py-20">
          <ProQuickReorder className="mb-10 text-[var(--oz-text)]" />
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--oz-gold)] font-semibold">Boutique professionnelle</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">Commandez Ozentya en gros, aux prix professionnels</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Pour les coiffeurs, salons, barbiers et revendeurs. Créez votre compte professionnel, choisissez vos lots et recevez vos factures automatiquement.</p>
          {!user && (
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/espace-pro/inscription" data-testid="pro-hero-register" className="px-6 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold text-sm">Créer un compte pro</Link>
              <Link to="/espace-pro/connexion" data-testid="pro-hero-login" className="px-6 py-3.5 rounded-full border border-white/30 font-semibold text-sm hover:border-white">Se connecter</Link>
            </div>
          )}
          <Perks config={config} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
        {user && !approved && <div className="mb-6"><PendingNotice /></div>}
        {univ && <p data-testid="pro-universe-filter" className="mb-4 inline-flex items-center gap-2 rounded-full bg-[var(--oz-gold)]/20 px-4 py-2 text-sm font-semibold">
          Univers : {univ.title}
          <button onClick={() => setParams({})} data-testid="pro-universe-clear" className="underline font-normal">retirer</button>
        </p>}
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`pro-filter-${c.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${cat === c ? "bg-[var(--oz-dark)] text-white border-[var(--oz-dark)]" : "border-[var(--oz-border)] text-[var(--oz-muted)] hover:border-[var(--oz-dark)]"}`}>
                {c}
              </button>
            ))}
          </div>
          <label className="relative md:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--oz-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit" data-testid="pro-search-input"
              className="w-full rounded-full border border-[var(--oz-border)] bg-white pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[var(--oz-gold)]" />
          </label>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {shown.map((p) => <ProductCard key={p.id} p={p} approved={approved} />)}
        </div>
        {products.length > 0 && shown.length === 0 && <p data-testid="pro-no-results" className="mt-10 text-center text-[var(--oz-muted)]">Aucun produit ne correspond à votre recherche.</p>}
      </section>
    </div>
  );
}
